import { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { BedDouble, CalendarRange, ExternalLink, Home, Hotel, ListChecks, LogOut, MessageSquare, Network, Receipt, Settings, Tag } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { http } from "@/lib/api";
import { useLang, useT } from "@/lib/i18n";
import { useAutoTranslate } from "@/lib/useAutoTranslate";

const NAV = [
  { to: "/extranet", label: "Beranda", icon: Home, end: true, id: "home" },
  { to: "/extranet/reservations", label: "Reservasi", icon: ListChecks, id: "reservations" },
  { to: "/extranet/rates", label: "Tarif & Ketersediaan", icon: CalendarRange, id: "rates" },
  { to: "/extranet/rooms", label: "Kamar & Rate Plan", icon: BedDouble, id: "rooms", manage: true },
  { to: "/extranet/promotions", label: "Promosi", icon: Tag, id: "promotions", manage: true },
  { to: "/extranet/property", label: "Properti", icon: Hotel, id: "property", manage: true },
  { to: "/extranet/channel", label: "Channel Manager", icon: Network, id: "channel", manage: true },
  { to: "/extranet/reviews", label: "Review", icon: MessageSquare, id: "reviews", manage: true },
  { to: "/extranet/commission", label: "Laporan Komisi", icon: Receipt, id: "commission", manage: true },
];

export default function ExtranetLayout() {
  const nav = useNavigate();
  const t = useT();
  const { lang, setLang } = useLang();
  useAutoTranslate();
  const [me, setMe] = useState(null);
  const [properties, setProperties] = useState([]);
  const [pid, setPid] = useState(localStorage.getItem("ex_pid") || "");

  const reload = useCallback(async (selectId) => {
    const { data } = await http.get("/extranet/properties");
    setProperties(data);
    setPid((cur) => {
      const next = selectId || (data.some((p) => p.id === cur) ? cur : data[0]?.id || "");
      localStorage.setItem("ex_pid", next);
      return next;
    });
  }, []);
  useEffect(() => {
    if (!localStorage.getItem("staff_token")) { nav("/extranet/login"); return; }
    http.get("/staff-auth/me").then((r) => { setMe(r.data); reload(); }).catch(() => { localStorage.removeItem("staff_token"); nav("/extranet/login"); });
  }, [nav, reload]);

  const select = (id) => { localStorage.setItem("ex_pid", id); setPid(id); };
  const logout = () => { localStorage.removeItem("staff_token"); nav("/extranet/login"); };
  const property = properties.find((p) => p.id === pid);
  const canManage = me && me.role !== "staff";
  const loc = useLocation();
  const blocked = me && !canManage && NAV.some((n) => n.manage && loc.pathname.startsWith(n.to));

  return (
    <div className="extranet min-h-screen bg-[#F0EDE8] text-[#1C1917]">
      <header className="bg-[#1C1917] text-white">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center gap-4">
            <span className="font-ex-heading text-lg font-extrabold tracking-tight">Bedsolving Connect <span className="font-medium text-[#D9A066]">extranet</span></span>
            <Select value={pid} onValueChange={select}>
              <SelectTrigger data-testid="property-switcher" className="h-9 w-[220px] border-white/20 bg-[#292524] text-white sm:w-[300px]"><SelectValue placeholder={t("Pilih properti")} /></SelectTrigger>
              <SelectContent>{properties.map((p) => <SelectItem key={p.id} value={p.id} data-testid={`property-option-${p.id}`}>{p.name} · {p.city}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <div className="flex overflow-hidden rounded border border-white/30 text-xs font-bold" data-testid="lang-toggle">
              {["id", "en"].map((l) => <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)} className={`px-2 py-1 uppercase ${lang === l ? "bg-[#D9A066] text-[#1C1917]" : "text-white/80"}`}>{l}</button>)}
            </div>
            {me && <NavLink to="/extranet/settings" data-testid="extranet-settings-button" className={({ isActive }) => `flex items-center gap-1.5 rounded px-3 py-1.5 font-medium transition-colors ${isActive ? "bg-white text-[#1C1917]" : "border border-white/30 hover:bg-white/10"}`}><Settings className="h-4 w-4" /> {t("Pengaturan")}</NavLink>}
            <Link to="/" data-testid="extranet-view-site" className="hidden items-center gap-1.5 rounded border border-white/30 px-3 py-1.5 font-medium hover:bg-white/10 md:flex"><ExternalLink className="h-4 w-4" /> {t("Lihat situs tamu")}</Link>
            <span className="hidden text-xs text-white/60 lg:inline" data-testid="staff-identity">{me?.email} · {me?.role}</span>
            <button data-testid="extranet-logout" onClick={logout} title={t("Keluar")} className="rounded p-2 hover:bg-white/10"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-[1400px] gap-1 overflow-x-auto px-4 sm:px-6 no-scrollbar">
          {NAV.filter((n) => !n.manage || canManage).map(({ to, label, icon: I, end, id }) => (
            <NavLink key={to} to={to} end={end} data-testid={`extranet-nav-${id}`}
              className={({ isActive }) => `flex shrink-0 items-center gap-2 border-b-[3px] px-4 py-3 text-sm font-semibold transition-colors ${isActive ? "border-[#D9A066] text-white" : "border-transparent text-white/65 hover:text-white"}`}>
              <I className="h-4 w-4" /> {t(label)}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6">
        {blocked ? <Navigate to="/extranet" replace /> : property && me ? <Outlet context={{ property, properties, reload, select, me }} key={property.id} /> :
          <p className="p-10 text-center text-slate-500">{t("Memuat properti...")}</p>}
      </main>
    </div>
  );
}
